/**
 * useCircularList – custom hook to manage the circular list rendering. Composes
 * useCircularNavigation for the keyboard-driven index/animation state, and turns that state
 * into the actual (possibly duplicated) list of rendered children.
 */
import Direction from '@shared/types/enums/direction';
import Screen from '@screen';
import { Fragment, ReactNode, useMemo } from 'react';
import useCircularNavigation, {
  CircularNavigationProps,
} from './useCircularNavigation';

/**
 * Constants
 *
 * RENDER_WINDOW_RADIUS: neighbors within this many positions of the current index render for
 * real; everything else becomes a same-sized placeholder, so only currently-visible items stay
 * mounted.
 */
const RENDER_WINDOW_RADIUS = 1;

/**
 * Props for the CircularList component.
 */
export interface CircularListProps<T> extends CircularNavigationProps<T> {
  offset: number;
  renderItem: (item: T, index: number) => ReactNode;
}

/**
 * Custom hook to manage the circular list data and functions.
 * @param props - Props for the CircularList component.
 * @returns {Object} An object containing the circular list data and functions.
 */
export default function useCircularList<T>({
  direction = Direction.HORIZONTAL,
  items,
  offset,
  renderItem,
  ...navigationProps
}: CircularListProps<T>) {
  const { duration, index, noDuplicates, internalToLogical, onTransitionEnd } =
    useCircularNavigation({ direction, items, ...navigationProps });

  const length = items?.length ?? 0;

  // When there are no duplicates: items as-is. Otherwise: duplicate first/last for seamless wrap animation.
  const circularItems = useMemo(() => {
    if (!length) return [];
    return noDuplicates ? items : [items[length - 1], ...items, items[0]];
  }, [noDuplicates, items, length]);

  const children = useMemo(() => {
    const placeholderSize = offset * Screen.widthRatio;
    const placeholderStyle =
      direction === Direction.HORIZONTAL
        ? { width: placeholderSize }
        : { height: placeholderSize };
    const lastIndex = circularItems.length - 1;
    return circularItems.map((item, i) => {
      const isWrapAnchor =
        !noDuplicates &&
        (i === 0 || i === 1 || i === length || i === lastIndex);
      const display =
        Math.abs(i - index) <= RENDER_WINDOW_RADIUS || isWrapAnchor;
      return (
        <Fragment key={i}>
          {display ? (
            renderItem(item, internalToLogical(i))
          ) : (
            <div style={placeholderStyle} />
          )}
        </Fragment>
      );
    });
  }, [
    circularItems,
    direction,
    index,
    length,
    noDuplicates,
    offset,
    internalToLogical,
    renderItem,
  ]);

  return {
    children,
    duration,
    index,
    onTransitionEnd,
  };
}
